'use client';

import { ChakraProvider, EnvironmentProvider } from '@chakra-ui/react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { system } from '@skin-seoul/design-system';
import { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';

type ThemeProviderProps = {
  children: React.ReactNode;
};

export function DesignSystemShadowProvider({ children }: ThemeProviderProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const hostElement = containerRef.current;

    let shadowRoot = hostElement.shadowRoot;
    if (!shadowRoot) {
      shadowRoot = hostElement.attachShadow({ mode: 'open' });
    }

    const rootEl = document.createElement('div');
    shadowRoot.appendChild(rootEl);

    const cache = createCache({
      key: 'css',
      container: shadowRoot,
    });

    const root = createRoot(rootEl);
    root.render(
      <EnvironmentProvider value={() => shadowRoot}>
        <CacheProvider value={cache}>
          <ChakraProvider value={system}>{children}</ChakraProvider>
        </CacheProvider>
      </EnvironmentProvider>,
    );

    // Cleanup
    return () => {
      root.unmount();
    };
  }, [children]);

  return <div ref={containerRef} />;
}
